import { css } from "@emotion/css";
import { Callout } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import type { ActionTemplateParameterResource, ActionTemplateResource, ProcessTemplateParameterResource } from "@octopusdeploy/octopus-server-client";
import { ProcessType, isImportableValueControlType } from "@octopusdeploy/octopus-server-client";
import pluralize from "pluralize";
import convertPropertyValueResourceToString from "~/components/convertPropertyValueResourceToString";
import type { AddStepTemplateResult } from "../projects/components/Process/Common/CommonProcessHelpers";
export type ImportStepTemplateResult = {
    stepTemplate: ActionTemplateResource;
    addedParameters: ProcessTemplateParameterResource[];
    duplicateParameters: ProcessTemplateParameterResource[];
    addResult: AddStepTemplateResult;
};
interface StepTemplateImportedCalloutProps {
    onClose: () => void;
    latestImportedStepTemplate: ImportStepTemplateResult;
    processType: ProcessType;
}
export function StepTemplateImportedCallout({ latestImportedStepTemplate: { addedParameters, duplicateParameters, stepTemplate, addResult }, onClose, processType }: StepTemplateImportedCalloutProps) {
    const anyNonTextParametersAdded = addedParameters.some((p) => !isImportableValueControlType(p.DisplaySettings["Octopus.ControlType"]));
    return (<Callout type={"information"} title={`Step template imported successfully`} canClose={true} onClose={onClose}>
            <p>
                The custom step template <strong>{stepTemplate.Name}</strong> was successfully imported and has been detached from the original step template.
            </p>
            <ul className={styles.stepTemplateCalloutList}>
                {addedParameters.length > 0 ? (<>
                        <li>
                            {pluralize("parameter", addedParameters.length, true)} {pluralize("was", addedParameters.length)} imported. {processType === ProcessType.ProjectTemplate ? "After commiting, check" : "Check"} the Parameters{" "}
                            {processType === ProcessType.ProjectTemplate ? "page" : "tab"} to confirm {addedParameters.length > 1 ? "their" : "its"} {pluralize("name", addedParameters.length)} and {pluralize("value", addedParameters.length)}.
                        </li>
                        {anyNonTextParametersAdded ? <li>Default values for account, certificate, worker pool, and sensitive parameters have been cleared.</li> : null}
                    </>) : null}
                {duplicateParameters.length > 0 ? (<li>
                        The following {pluralize("parameter", duplicateParameters.length)} {pluralize("was", duplicateParameters.length)} not imported as there is already a parameter with the same name:
                        <ul className={styles.unimportedParametersList}>
                            {duplicateParameters.map((p) => (<li key={p.Name}>{p.Name}</li>))}
                        </ul>
                    </li>) : null}
                {addResult.anyAzureAccountsCleared ? <li>Azure account usages have been cleared and will need to be reconfigured.</li> : null}
                {addResult.anyGitCredentialsCleared ? <li>Git credentials have been cleared and will need to be reconfigured.</li> : null}
                {addResult.anyImmediatePackageReferencesCleared ? <li>Selected packages have been cleared and will need to be reconfigured.</li> : null}
                {addResult.anySensitiveValuesCleared ? <li>Sensitive values have been cleared and will need to be reconfigured.</li> : null}
            </ul>
        </Callout>);
}
const styles = {
    stepTemplateCalloutList: css({
        listStyleType: "disc",
        listStylePosition: "inside",
        margin: 0,
    }),
    unimportedParametersList: css({
        listStyleType: "circle",
        listStylePosition: "inside",
        paddingLeft: space[16],
        margin: 0,
    }),
};
export function convertActionTemplateParametersToTemplateParameters(actionTemplateParameters: ActionTemplateParameterResource[]): ProcessTemplateParameterResource[] {
    return actionTemplateParameters.map((param) => {
        const { DefaultValue, ...paramWithoutDefaults } = param;
        return {
            ...paramWithoutDefaults,
            // Default values for sensitive and resource-typed parameters can't be imported
            Values: isImportableValueControlType(param.DisplaySettings["Octopus.ControlType"]) && DefaultValue ? [{ Value: convertPropertyValueResourceToString(DefaultValue), Scope: {} }] : [],
            IsOptional: false,
        };
    });
}
